import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

// Hard upper bound used for image size validation only. Per-tier daily/weekly
// quota is enforced server-side via the `consume_analyst_credit` Postgres
// function (plan-aware), see src/integrations/supabase/migrations.
const MAX_IMAGE_BYTES = 6 * 1024 * 1024; // ~6 MB decoded

const SMC_SYSTEM_PROMPT = `You are the proprietary AI Analyst for XAU Pro Academy. Your task is to identify 'Smart Money' footprints on gold (XAUUSD) and other financial charts. You must ignore retail patterns (Trendlines, RSI, Support/Resistance) and focus strictly on Liquidity and Market Structure.

[RULE 1: MARKET STRUCTURE]
- Identify the Swing Structure (Higher Highs/Lows vs Lower Highs/Lows).
- Mark BOS (Break of Structure) only when the candle body closes beyond the previous high/low.
- Mark CHoCH (Change of Character) as the first sign of a potential trend reversal.
- Differentiate between a real BOS and a Liquidity Sweep (wick only).

[RULE 2: LIQUIDITY MAPPING]
- Locate BSL (Buy Side Liquidity) above equal highs and SSL (Sell Side Liquidity) below equal lows.
- Identify IDM (Inducement): The first internal pullback after a BOS. Price MUST take inducement before we look for a valid POI (Point of Interest).
- Look for Engineering Liquidity: Liquidity built just before a major Order Block to trap early buyers/sellers.

[RULE 3: SUPPLY & DEMAND (POI)]
- Valid Order Block (OB): The last opposite candle before a strong impulsive move that created a BOS and left a Fair Value Gap (FVG).
- High Probability OB: Must have 1) Swept previous liquidity, 2) Created an FVG, 3) Caused a BOS.
- Fair Value Gap (FVG): Identify imbalances where price moved too fast. These act as magnets for price re-balancing.

[RULE 4: XAUUSD SPECIFIC BEHAVIOR]
- Gold is highly sensitive to London & New York Open.
- Look for the 'Judas Swing': A false move at the session open that sweeps liquidity before the real trend begins.
- Focus on the M15 structure for entries and H1/H4 for overall bias.

[RULE 5: ORDER TYPE]
- MARKET: enter at current price (price is already at the POI, no retracement needed).
- LIMIT: wait for a retracement back into an Order Block, FVG, or discount/premium zone. This is the most common SMC entry.
- STOP: enter on a breakout beyond a key level after a confirmed liquidity sweep + BOS.
- If direction=WAIT, set order_type=null.

[RULE 6: TRADING STYLE]
Set trading_style based on BOTH the visible chart timeframe AND the target distance (pips / risk-reward). Do not infer from timeframe alone.
- SCALP: very short hold (minutes), small TP distance. Typical when entries are on M1 to M15 AND TP is close (XAUUSD: under 50 pips, FX majors: under 15 pips).
- INTRADAY: closed within the same session. Typical on M30 to H2 with a moderate TP (XAUUSD: roughly 50 to 200 pips, FX majors: 15 to 60 pips).
- SWING: held across sessions or days. Typical on H4 to Daily, OR any timeframe where TP distance is large (XAUUSD: over 200 pips, FX majors: over 60 pips).
Example: a 15M entry on XAUUSD with a 300 pip TP is SWING, not SCALP. Pip distance overrides timeframe when they disagree.
If you cannot tell, set trading_style=null.

[OUTPUT]
Call the report_analysis function with the structured analysis. Always set the 'asset' field to the instrument ticker visible on the chart (e.g. 'XAUUSD', 'GBPUSD', 'EURUSD', 'BTCUSD', 'US30'), uppercase with no separators. Use null only if you cannot read it. Use null for other fields you cannot determine from the chart. If the image is not a tradable chart, set bias=NEUTRAL, summary='This image does not appear to be a tradable chart.', direction=WAIT, and leave entry_zone, sl, and tp empty.

Grade confidence_pct as an integer between 5 and 95 based on how many institutional confluences are present:
- 80 to 95: liquidity sweep + clean BOS/CHoCH + valid unmitigated POI + HTF alignment + good session timing.
- 65 to 79: 3 of those confluences present, one missing or weak.
- 45 to 64: only 2 confluences, or structure is unclear, or POI already mitigated.
- 20 to 44: weak setup, conflicting structure, or price already past the POI.
- 5 to 19: not a tradable setup.
Vary the number based on what you actually see. Do not default to round numbers like 60 or 80. Pick the value that matches the evidence (e.g. 62, 71, 83, 47).

Do not give financial advice. Quote price levels only when clearly readable on the chart.`;

const analyzeInput = z.object({
  imageBase64: z
    .string()
    .min(100)
    .refine((s) => (s.length * 3) / 4 <= MAX_IMAGE_BYTES, {
      message: "Image too large (max 6 MB).",
    }),
  mediaType: z.enum(["image/png", "image/jpeg", "image/webp"]),
});

const reportSchema = z.object({
  asset: z
    .string()
    .min(1)
    .max(20)
    .nullable()
    .default(null)
    .transform((v) => (v ? v.toUpperCase().replace(/[^A-Z0-9]/g, "") : null)),
  bias: z.enum(["BULLISH", "BEARISH", "NEUTRAL"]),
  confidence_pct: z.number().int().min(5).max(95),
  key_zone: z.string().min(1).max(120),
  summary: z.string().min(1).max(280),
  trade_setup: z.object({
    direction: z.enum(["LONG", "SHORT", "WAIT"]),
    order_type: z.enum(["MARKET", "LIMIT", "STOP"]).nullable().default(null),
    entry_zone: z.string().max(80).nullable(),
    tp: z.array(z.string().max(80)).max(4).default([]),
    sl: z.string().max(80).nullable(),
    invalidation: z.string().max(200).nullable(),
    trading_style: z
      .enum(["SCALP", "INTRADAY", "SWING"])
      .nullable()
      .optional()
      .catch(null)
      .transform((v) => v ?? null),
  }),
  structure: z.array(z.string().max(240)).max(8).default([]),
  liquidity: z.array(z.string().max(240)).max(8).default([]),
  reasoning: z.string().min(1).max(800),
});

export type ConfidenceLabel = "HIGH" | "MEDIUM" | "LOW";

export function deriveConfidenceLabel(pct: number): ConfidenceLabel {
  if (pct >= 75) return "HIGH";
  if (pct >= 50) return "MEDIUM";
  return "LOW";
}

export type AnalystReport = z.infer<typeof reportSchema> & {
  confidence: ConfidenceLabel;
};

export type AnalystPlan = {
  plan_key: "free" | "standard" | "pro" | "elite";
  plan_name: string;
  plan_limit: number;
  period: "day" | "week";
};

type QuotaSnapshot = AnalystPlan & {
  remaining: number;
  used: number;
};

type AnalyzeResult =
  | { ok: true; report: AnalystReport; quota: QuotaSnapshot }
  | {
      ok: false;
      error: "quota_exhausted" | "missing_key" | "ai_error" | "rate_limited" | "credits_required";
      message: string;
      quota: QuotaSnapshot;
    };

const readNum = (value: unknown, fallback: number) =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const DEFAULT_PLAN: AnalystPlan = {
  plan_key: "free",
  plan_name: "Foundations Member",
  plan_limit: 1,
  period: "week",
};

function pickPlan(row: any): AnalystPlan {
  return {
    plan_key: (row?.plan_key as AnalystPlan["plan_key"]) ?? DEFAULT_PLAN.plan_key,
    plan_name: (row?.plan_name as string) ?? DEFAULT_PLAN.plan_name,
    plan_limit: readNum(row?.plan_limit, DEFAULT_PLAN.plan_limit),
    period: (row?.period as AnalystPlan["period"]) ?? DEFAULT_PLAN.period,
  };
}

// Cost-tiered model selection: Free users get Flash (fast, cheap),
// paid tiers get Pro (deeper SMC reasoning + sharper vision).
const FLASH_MODEL = "google/gemini-3-flash-preview";
const PRO_MODEL = "google/gemini-3.1-pro-preview";
function pickModel(planKey: string): string {
  return planKey === "free" ? FLASH_MODEL : PRO_MODEL;
}

async function readQuota(userId: string): Promise<QuotaSnapshot> {
  const { data } = await supabaseAdmin.rpc("get_analyst_quota", { _user: userId });
  const row = Array.isArray(data) ? data[0] : data;
  const plan = pickPlan(row);
  return {
    ...plan,
    remaining: readNum(row?.remaining, plan.plan_limit),
    used: readNum(row?.used, 0),
  };
}

function refundedSnapshot(snapshot: QuotaSnapshot): QuotaSnapshot {
  return {
    ...snapshot,
    used: Math.max(0, snapshot.used - 1),
    remaining: Math.min(snapshot.plan_limit, snapshot.remaining + 1),
  };
}

function quotaExhaustedMessage(plan: AnalystPlan): string {
  const periodWord = plan.period === "week" ? "Weekly" : "Daily";
  return `${periodWord} limit reached for your ${plan.plan_name} plan. Upgrade to get more analyses!`;
}

const reportTool = {
  type: "function" as const,
  function: {
    name: "report_analysis",
    description:
      "Return a structured Smart Money Concept analysis of the supplied chart image.",
    parameters: {
      type: "object",
      additionalProperties: false,
      properties: {
        asset: {
          type: ["string", "null"],
          description:
            "The instrument ticker visible on the chart, uppercase, no separators (e.g. 'XAUUSD', 'GBPUSD', 'EURUSD', 'BTCUSD', 'US30'). Null if not clearly identifiable.",
        },
        bias: {
          type: "string",
          enum: ["BULLISH", "BEARISH", "NEUTRAL"],
          description: "Overall directional bias derived from market structure.",
        },
        confidence_pct: {
          type: "integer",
          minimum: 5,
          maximum: 95,
          description:
            "Confidence as an integer 5 to 95 reflecting how many institutional confluences are present (sweep, BOS/CHoCH, valid POI, HTF alignment, session timing). Vary the value, do not default to round numbers like 60 or 80.",
        },
        key_zone: {
          type: "string",
          description:
            "The single most important zone to watch (Order Block, FVG, or liquidity level) with timeframe if visible. Short label.",
        },
        summary: {
          type: "string",
          description: "One-sentence headline describing the setup.",
        },
        trade_setup: {
          type: "object",
          additionalProperties: false,
          properties: {
            direction: { type: "string", enum: ["LONG", "SHORT", "WAIT"] },
            order_type: {
              type: ["string", "null"],
              enum: ["MARKET", "LIMIT", "STOP", null],
              description:
                "Execution order type. MARKET when price is at the POI. LIMIT when waiting for a retracement to an OB/FVG. STOP when entering on a breakout. Null when direction is WAIT.",
            },
            entry_zone: {
              type: ["string", "null"],
              description: "Entry zone as a price range, e.g. '2,345.50 to 2,348.00'. Null if not actionable.",
            },
            tp: {
              type: "array",
              items: { type: "string" },
              description: "Targets, e.g. ['TP1: 2,360.00', 'TP2: 2,375.00']. Empty array if not actionable.",
            },
            sl: {
              type: ["string", "null"],
              description: "Stop loss price level. Null if not actionable.",
            },
            invalidation: {
              type: ["string", "null"],
              description: "What would invalidate this setup (one short sentence).",
            },
            trading_style: {
              type: ["string", "null"],
              enum: ["SCALP", "INTRADAY", "SWING", null],
              description:
                "Trading style. Decide using BOTH the chart timeframe AND the TP pip distance. SCALP=minutes/small TP. INTRADAY=same session/moderate TP. SWING=multi-session or large TP. Null if unclear.",
            },
          },
          required: ["direction", "order_type", "entry_zone", "tp", "sl", "invalidation", "trading_style"],
        },
        structure: {
          type: "array",
          items: { type: "string" },
          description:
            "Bullet points about market structure: BOS / CHoCH / swing context. Max 6 bullets.",
        },
        liquidity: {
          type: "array",
          items: { type: "string" },
          description:
            "Bullet points about liquidity: BSL / SSL / IDM / engineered liquidity / draw on liquidity. Max 6 bullets.",
        },
        reasoning: {
          type: "string",
          description:
            "2-4 sentences explaining the institutional logic behind the bias and setup.",
        },
      },
      required: [
        "asset",
        "bias",
        "confidence_pct",
        "key_zone",
        "summary",
        "trade_setup",
        "structure",
        "liquidity",
        "reasoning",
      ],
    },
  },
};

export const analyzeChart = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => analyzeInput.parse(data))
  .handler(async ({ data, context }): Promise<AnalyzeResult> => {
    const userId = context.userId;
    const apiKey = process.env.LOVABLE_API_KEY;

    if (!apiKey) {
      return {
        ok: false,
        error: "missing_key",
        message: "AI service is not configured.",
        quota: await readQuota(userId),
      };
    }

    // Consume credit (atomic, plan-aware)
    const { data: consumeRows, error: consumeError } = await supabaseAdmin.rpc(
      "consume_analyst_credit",
      { _user: userId },
    );
    if (consumeError) {
      console.error("consume_analyst_credit failed:", consumeError);
      return {
        ok: false,
        error: "ai_error",
        message: "Could not check your plan quota. Please try again.",
        quota: await readQuota(userId),
      };
    }
    const row = Array.isArray(consumeRows) ? consumeRows[0] : consumeRows;
    const plan = pickPlan(row);
    if (!row?.allowed) {
      const exhausted = await readQuota(userId);
      return {
        ok: false,
        error: "quota_exhausted",
        message: quotaExhaustedMessage(plan),
        quota: exhausted,
      };
    }

    // Snapshot after the credit was consumed.
    const consumed: QuotaSnapshot = {
      ...plan,
      remaining: readNum(row.remaining, Math.max(plan.plan_limit - 1, 0)),
      used: plan.plan_limit - readNum(row.remaining, plan.plan_limit - 1),
    };

    const selectedModel = pickModel(plan.plan_key);

    try {
      const resp = await fetch("https://ai.gateway.lovable.dev/v1/chat/completions", {
        method: "POST",
        headers: {
          authorization: `Bearer ${apiKey}`,
          "content-type": "application/json",
        },
        body: JSON.stringify({
          model: selectedModel,
          // Gemini 3.x Pro is a thinking model and spends ~1000-1400 tokens
          // on hidden reasoning before emitting the tool call. A tight cap
          // makes it finish on `length` with no tool_calls and no content,
          // which surfaces as "AI returned an empty response". 4000 leaves
          // headroom for reasoning + the structured tool call payload.
          max_tokens: 4000,
          messages: [
            { role: "system", content: SMC_SYSTEM_PROMPT },
            {
              role: "user",
              content: [
                {
                  type: "text",
                  text: "Analyze this chart with the XAU Pro SMC framework and call report_analysis with the structured result.",
                },
                {
                  type: "image_url",
                  image_url: { url: `data:${data.mediaType};base64,${data.imageBase64}` },
                },
              ],
            },
          ],
          tools: [reportTool],
          tool_choice: { type: "function", function: { name: "report_analysis" } },
        }),
      });

      if (!resp.ok) {
        const errBody = await resp.text();
        console.error("Lovable AI Gateway error:", resp.status, errBody);
        await supabaseAdmin.rpc("refund_analyst_credit", { _user: userId });
        const refunded = refundedSnapshot(consumed);
        if (resp.status === 429) {
          return {
            ok: false,
            error: "rate_limited",
            message: "AI rate limit reached. Please try again in a minute. Your credit was refunded.",
            quota: refunded,
          };
        }
        if (resp.status === 402) {
          return {
            ok: false,
            error: "credits_required",
            message: "AI credits are unavailable right now. Your analysis credit was refunded.",
            quota: refunded,
          };
        }
        return {
          ok: false,
          error: "ai_error",
          message: "Could not analyze chart. Your credit was refunded. Please try again.",
          quota: refunded,
        };
      }

      const json = (await resp.json()) as {
        choices?: Array<{
          message?: {
            content?: string | Array<{ type?: string; text?: string }>;
            tool_calls?: Array<{
              function?: { name?: string; arguments?: string };
            }>;
          };
        }>;
      };

      const message = json.choices?.[0]?.message;
      const toolCall = message?.tool_calls?.[0];
      const args = toolCall?.function?.arguments;

      let parsed: z.infer<typeof reportSchema> | null = null;
      if (args) {
        try {
          parsed = reportSchema.parse(JSON.parse(args));
        } catch (e) {
          console.error("Tool-call JSON failed schema validation:", e, args);
        }
      }

      // Fallback: model returned text instead of a tool call.
      if (!parsed) {
        const content = message?.content;
        const text = Array.isArray(content)
          ? content
              .filter((b) => b.type === "text" && typeof b.text === "string")
              .map((b) => b.text)
              .join("\n\n")
              .trim()
          : (content ?? "").trim();

        if (text) {
          parsed = {
            asset: null,
            bias: "NEUTRAL",
            confidence_pct: 30,
            key_zone: "—",
            summary: text.slice(0, 240),
            trade_setup: {
              direction: "WAIT",
              order_type: null,
              entry_zone: null,
              tp: [],
              sl: null,
              invalidation: null,
              trading_style: null,
            },
            structure: [],
            liquidity: [],
            reasoning: text,
          };
        }
      }

      if (!parsed) {
        await supabaseAdmin.rpc("refund_analyst_credit", { _user: userId });
        return {
          ok: false,
          error: "ai_error",
          message: "The AI returned an empty response. Your credit was refunded.",
          quota: refundedSnapshot(consumed),
        };
      }

      const report: AnalystReport = {
        ...parsed,
        confidence: deriveConfidenceLabel(parsed.confidence_pct),
      };

      // Persist to history (non-fatal on failure).
      try {
        const { error: insertError } = await supabaseAdmin
          .from("analysis_history")
          .insert([
            {
              user_id: userId,
              asset: report.asset ?? "XAUUSD",
              bias: report.bias,
              direction: report.trade_setup.direction,
              confidence: report.confidence,
              confidence_pct: report.confidence_pct,
              key_zone: report.key_zone,
              summary: report.summary,
              entry_zone: report.trade_setup.entry_zone,
              tp: report.trade_setup.tp,
              sl: report.trade_setup.sl,
              invalidation: report.trade_setup.invalidation,
              raw_json_response: JSON.parse(
                JSON.stringify({ ...report, _model: selectedModel, _plan_key: plan.plan_key }),
              ),
            },
          ]);
        if (insertError) {
          console.error("analysis_history insert failed:", insertError);
        }
      } catch (e) {
        console.error("analysis_history insert threw:", e);
      }

      return { ok: true, report, quota: consumed };
    } catch (e) {
      console.error("analyzeChart unexpected error:", e);
      await supabaseAdmin.rpc("refund_analyst_credit", { _user: userId });
      return {
        ok: false,
        error: "ai_error",
        message: "Unexpected error contacting the AI. Your credit was refunded.",
        quota: refundedSnapshot(consumed),
      };
    }
  });

export const getAnalystQuota = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<QuotaSnapshot> => {
    return readQuota(context.userId);
  });

export type AnalysisHistoryRow = {
  id: string;
  created_at: string;
  asset: string;
  bias: AnalystReport["bias"];
  direction: AnalystReport["trade_setup"]["direction"];
  confidence: AnalystReport["confidence"];
  confidence_pct: number;
  key_zone: string | null;
  summary: string | null;
  entry_zone: string | null;
  tp: string[];
  sl: string | null;
  invalidation: string | null;
  raw_json_response: AnalystReport;
};

export const getAnalystHistory = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AnalysisHistoryRow[]> => {
    const { supabase } = context;
    const { data, error } = await supabase
      .from("analysis_history")
      .select(
        "id, created_at, asset, bias, direction, confidence, confidence_pct, key_zone, summary, entry_zone, tp, sl, invalidation, raw_json_response",
      )
      .order("created_at", { ascending: false })
      .limit(5);
    if (error) {
      console.error("getAnalystHistory failed:", error);
      return [];
    }
    return (data ?? []) as unknown as AnalysisHistoryRow[];
  });

